import difference from 'lodash/difference';
import find from 'lodash/find';
import compose from 'lodash/fp/compose';
import filterFp from 'lodash/fp/filter';
import uniqBy from 'lodash/uniqBy';
import PropTypes from 'prop-types';
import React, { createContext, useCallback, useContext, useEffect, useMemo, useReducer, useState } from 'react';

import { useFilter } from '@/contexts/FilterContext';

const TableFiltersContext = createContext();

const initialState = { activeFilters: [], filtersValue: [] };

const updateSettingFilters = (table, value = initialState) => {
  value.filters = table;
  localStorage.setItem(table, JSON.stringify(value));
  return value;
};

const getInitial = (id, filters) => {
  id = id || window?.location?.pathname;
  const filters_table = `__table_filters_${id}`;
  try {
    const settings = localStorage.getItem(filters_table);
    if (settings) {
      let settingTable = JSON.parse(settings);
      if (settingTable.filters === filters_table) {
        if (settingTable.activeFilters)
          settingTable.activeFilters = settingTable.activeFilters.map(
            (
              filter => filters.find(filterOriginal => filter.name === filterOriginal.name)
            )
          ).filter(f => !!f);
        settingTable = Object.assign({}, initialState, settingTable);
        return settingTable;
      }
    }
    return updateSettingFilters(filters_table);
  } catch (e) {
    return updateSettingFilters();
  }
};

function reducer (state, action) {
  let finalState = state;
  switch (action.type) {
    case 'activeFilters':
      state.activeFilters = [...action.payload.activeFilters];
      finalState = { ...state };
      break;
    case 'filtersValue':
      state.filtersValue = [...action.payload.filtersValue];
      finalState = { ...state };
      break;
    default:
      throw new Error();
  }
  return updateSettingFilters(finalState.filters, finalState);
}

function TableFiltersProvider ({ id, filters, ...props }) {
  const { setFilter } = useFilter();
  // eslint-disable-next-line no-unused-vars
  const [state, dispatch] = useReducer(reducer, getInitial(id, filters));
  const [activeFilters, setActiveFilters] = useState([...state?.activeFilters]);
  const [filtersValue, setFiltersValue] = useState([...state?.filtersValue]);

  //Filter item logic
  const addActiveFilter = useCallback((f) => {
    const current = [f, ...activeFilters];
    setActiveFilters(current);
    //
    dispatch({ type: 'activeFilters', payload: { activeFilters: current } });
  }, [activeFilters]);

  const onRemoveActiveFilter = useCallback(({ name }) => {
    const current = activeFilters.filter(f => f?.name !== name);
    setActiveFilters(current);
    //
    dispatch({ type: 'activeFilters', payload: { activeFilters: current } });
  }, [activeFilters]);

  const onClearAllActiveFilter = useCallback(() => {
    setActiveFilters([]);
    //
    dispatch({ type: 'activeFilters', payload: { activeFilters: [] } });
  }, []);

  const inactiveFilters = useMemo(() => difference(filters, activeFilters, 'name'), [activeFilters, filters]);

  //Filter value logic
  const onChangeFilter = useCallback((_filter) => {
    const current = uniqBy([_filter, ...filtersValue], 'name');
    setFiltersValue(current);
    dispatch({ type: 'filtersValue', payload: { filtersValue: current } });
  }, [filtersValue]);

  useEffect(() => {
    compose(
      setFiltersValue,
      filterFp(({ name }) => find(activeFilters, ({ name }))),
    )(filtersValue);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeFilters]);

  useEffect(() => {
    if (filtersValue.length) {
      setFilter && setFilter({
        type: 'AND',
        filters: filtersValue.map(({ value }) => value),
      });
    } else {
      setFilter && setFilter(null);
    }
  }, [filtersValue, setFilter]);

  return (
    <TableFiltersContext.Provider value={{
      filters,
      activeFilters,
      setActiveFilters,
      filtersValue,
      setFiltersValue,
      addActiveFilter,
      onRemoveActiveFilter,
      onClearAllActiveFilter,
      inactiveFilters,
      onChangeFilter,
    }} {...props} />
  );
}

function useTableFilters () {
  const context = useContext(TableFiltersContext);
  if (context === undefined) {
    return {};
  }
  const {
    filters,
    activeFilters,
    setActiveFilters,
    filtersValue,
    setFiltersValue,
    addActiveFilter,
    onRemoveActiveFilter,
    onClearAllActiveFilter,
    inactiveFilters,
    onChangeFilter,
  } = context;

  return {
    filters,
    activeFilters,
    setActiveFilters,
    filtersValue,
    setFiltersValue,
    addActiveFilter,
    onRemoveActiveFilter,
    onClearAllActiveFilter,
    inactiveFilters,
    onChangeFilter,
  };
}

TableFiltersProvider.propTypes = {
  id: PropTypes.string,
  filters: PropTypes.array,
};

TableFiltersProvider.defaultProps = {
  filters: [],
};

export { TableFiltersProvider, useTableFilters };
